"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { SiTrustpilot, SiAmd, SiIntel } from "react-icons/si";
import Image from "next/image";
import { FiCpu, FiShield, FiHardDrive, FiZap, FiServer } from "react-icons/fi";
import { LuMemoryStick } from "react-icons/lu";
import { BsAmd } from "react-icons/bs";
import { CurrencySelector, useCurrencyRate, formatPrice, type Currency } from "@/app/Components/CurrencySelector";
import ResourceCalculator from "@/app/Components/ResourceCalculator";

const ENABLE_INFINITE_LOOP = false;

const TRUSTPILOT_RATING = 4.7;
const TRUSTPILOT_REVIEWS = 37;
const TRUSTPILOT_MAX_STARS = 5;

const billingPeriodLabel: Record<string, string> = {
    monthly: "/mo",
    quarterly: "/qtr",
    yearly: "/yr",
};

type Tier = "grass" | "nether" | "diamond";

const tierConfig: Record<Tier, {
    label: string;
    rank: string;
    cardGradient: string;
    border: string;
    accent: string;
    priceColor: string;
    buttonClass: string;
    badgeClass: string;
    dotColor: string;
    iconBg: string;
    glowColor: string;
    hoverShadow: string;
    logoRingColor: string;
}> = {
    grass: {
        label: "Starter",
        rank: "Top 3 Plan",
        cardGradient: "bg-gradient-to-b from-purple-400/10 to-card",
        border: "border-purple-400/10",
        accent: "text-purple-400",
        priceColor: "text-purple-400",
        buttonClass: "bg-purple-400/10 text-purple-400 hover:bg-purple-400/20 border border-purple-400/40",
        badgeClass: "bg-purple-400/10 border border-purple-400/40 text-purple-400",
        dotColor: "bg-purple-400",
        iconBg: "bg-purple-400/15",
        glowColor: "rgba(192,132,252,0.35)",
        hoverShadow: "0 0 0 2px rgba(192,132,252,0.45), inset 0 0 24px rgba(192,132,252,0.08)",
        logoRingColor: "rgba(192,132,252,0.5)",
    },
    nether: {
        label: "Prime",
        rank: "Top 2 Plan",
        cardGradient: "bg-gradient-to-b from-purple-500/10 to-card",
        border: "border-purple-500/10",
        accent: "text-purple-500",
        priceColor: "text-purple-500",
        buttonClass: "bg-purple-500/10 text-purple-500 hover:bg-purple-500/20 border border-purple-500/40",
        badgeClass: "bg-purple-500/10 border border-purple-500/40 text-purple-500",
        dotColor: "bg-purple-500",
        iconBg: "bg-purple-500/15",
        glowColor: "rgba(168,85,247,0.35)",
        hoverShadow: "0 0 0 2px rgba(168,85,247,0.5), inset 0 0 28px rgba(168,85,247,0.1)",
        logoRingColor: "rgba(168,85,247,0.6)",
    },
    diamond: {
        label: "Extreme",
        rank: "Top 1 Plan",
        cardGradient: "bg-gradient-to-b from-purple-700/10 to-card",
        border: "border-purple-700/10",
        accent: "text-purple-700",
        priceColor: "text-purple-700",
        buttonClass: "bg-purple-700/10 text-purple-600 hover:bg-purple-700/20 border border-purple-700/40",
        badgeClass: "bg-purple-700/10 border border-purple-700/40 text-purple-600",
        dotColor: "bg-purple-700",
        iconBg: "bg-purple-700/15",
        glowColor: "rgba(126,34,206,0.4)",
        hoverShadow: "0 0 0 2px rgba(126,34,206,0.55), inset 0 0 32px rgba(126,34,206,0.12)",
        logoRingColor: "rgba(126,34,206,0.65)",
    },
};

const packages: {
    id: number;
    name: string;
    tier: Tier;
    prices: { monthly: number; quarterly: number; yearly: number };
    logoImage: string;
    specs: { icon: string; label: string; superscript?: string; tooltip?: string }[];
    popular: boolean;
    outOfStock?: boolean;
    checkoutId?: number;
}[] = [
        {
            id: 1,
            name: "1GB RAM",
            tier: "grass",
            prices: { monthly: 400, quarterly: 1200, yearly: 4800 },
            logoImage: "https://cdn.aurex.lk/minecraft-servers/plan_1gb.webp",
            specs: [
                { icon: "cpu", label: "AMD Ryzen™ 7 5800X", superscript: "3" },
                { icon: "bolt", label: "0.5 vCore @ ~4.7 GHz" },
                { icon: "memory", label: "1GB DDR5 RAM" },
                { icon: "storage", label: "10GB NVMe Storage", tooltip: "Free storage upgrades upto 400GB under fair usage policy." },
                { icon: "port", label: "5 Port Allocations" },
                { icon: "shield", label: "Enterprise Game DDoS Protection" },
            ],
            popular: false,
            checkoutId: 44,
        },
        {
            id: 2,
            name: "2GB RAM",
            tier: "grass",
            prices: { monthly: 800, quarterly: 2400, yearly: 9600 },
            logoImage: "https://cdn.aurex.lk/minecraft-servers/plan_2gb.webp",
            specs: [
                { icon: "cpu", label: "AMD Ryzen™ 7 5800X", superscript: "3" },
                { icon: "bolt", label: "1 vCore @ ~4.7 GHz" },
                { icon: "memory", label: "2GB DDR5 RAM" },
                { icon: "storage", label: "20GB NVMe Storage", tooltip: "Free storage upgrades upto 400GB under fair usage policy." },
                { icon: "port", label: "5 Port Allocations" },
                { icon: "shield", label: "Enterprise Game DDoS Protection" },
            ],
            popular: false,
            checkoutId: 45,
        },
        {
            id: 3,
            name: "3GB RAM",
            tier: "grass",
            prices: { monthly: 1200, quarterly: 3600, yearly: 14400 },
            logoImage: "https://cdn.aurex.lk/minecraft-servers/plan_3gb.webp",
            specs: [
                { icon: "cpu", label: "AMD Ryzen™ 7 5800X", superscript: "3" },
                { icon: "bolt", label: "1 vCore @ ~4.7 GHz" },
                { icon: "memory", label: "3GB DDR5 RAM" },
                { icon: "storage", label: "30GB NVMe Storage", tooltip: "Free storage upgrades upto 400GB under fair usage policy." },
                { icon: "port", label: "5 Port Allocations" },
                { icon: "shield", label: "Enterprise Game DDoS Protection" },
            ],
            popular: false,
            checkoutId: 109,
        },
        {
            id: 4,
            name: "4GB RAM",
            tier: "grass",
            prices: { monthly: 1600, quarterly: 4800, yearly: 19200 },
            logoImage: "https://cdn.aurex.lk/minecraft-servers/plan_4gb.webp",
            specs: [
                { icon: "cpu", label: "AMD Ryzen™ 7 5800X", superscript: "3" },
                { icon: "bolt", label: "1 vCore @ ~4.7 GHz" },
                { icon: "memory", label: "4GB DDR5 RAM" },
                { icon: "storage", label: "40GB NVMe Storage", tooltip: "Free storage upgrades upto 400GB under fair usage policy." },
                { icon: "port", label: "5 Port Allocations" },
                { icon: "shield", label: "Enterprise Game DDoS Protection" },
            ],
            popular: false,
            checkoutId: 46,
        },
        {
            id: 5,
            name: "6GB RAM",
            tier: "grass",
            prices: { monthly: 2400, quarterly: 7200, yearly: 28800 },
            logoImage: "https://cdn.aurex.lk/minecraft-servers/plan_6gb.webp",
            specs: [
                { icon: "cpu", label: "AMD Ryzen™ 7 5800X", superscript: "3" },
                { icon: "bolt", label: "1.5 vCores @ ~4.7 GHz" },
                { icon: "memory", label: "6GB DDR5 RAM" },
                { icon: "storage", label: "60GB NVMe Storage", tooltip: "Free storage upgrades upto 400GB under fair usage policy." },
                { icon: "port", label: "5 Port Allocations" },
                { icon: "shield", label: "Enterprise Game DDoS Protection" },
            ],
            popular: false,
            checkoutId: 47,
        },
        {
            id: 6,
            name: "8GB RAM",
            tier: "nether",
            prices: { monthly: 3200, quarterly: 9600, yearly: 38400 },
            logoImage: "https://cdn.aurex.lk/minecraft-servers/plan_8gb.webp",
            specs: [
                { icon: "cpu", label: "AMD Ryzen™ 7 5800X", superscript: "3" },
                { icon: "bolt", label: "2 vCores @ ~4.7 GHz" },
                { icon: "memory", label: "8GB DDR5 RAM" },
                { icon: "storage", label: "60GB NVMe Storage", tooltip: "Free storage upgrades upto 400GB under fair usage policy." },
                { icon: "port", label: "5 Port Allocations" },
                { icon: "shield", label: "Enterprise Game DDoS Protection" },
            ],
            popular: true,
            checkoutId: 48,
        },
        {
            id: 7,
            name: "10GB RAM",
            tier: "nether",
            prices: { monthly: 4000, quarterly: 12000, yearly: 48000 },
            logoImage: "https://cdn.aurex.lk/minecraft-servers/plan_10gb.webp",
            specs: [
                { icon: "cpu", label: "AMD Ryzen™ 7 5800X", superscript: "3" },
                { icon: "bolt", label: "2 vCores @ ~4.7 GHz" },
                { icon: "memory", label: "10GB DDR5 RAM" },
                { icon: "storage", label: "60GB NVMe Storage", tooltip: "Free storage upgrades upto 400GB under fair usage policy." },
                { icon: "port", label: "5 Port Allocations" },
                { icon: "shield", label: "Enterprise Game DDoS Protection" },
            ],
            popular: false,
            checkoutId: 49,
        },
        {
            id: 8,
            name: "12GB RAM",
            tier: "nether",
            prices: { monthly: 4800, quarterly: 14400, yearly: 57600 },
            logoImage: "https://cdn.aurex.lk/minecraft-servers/plan_12gb.webp",
            specs: [
                { icon: "cpu", label: "AMD Ryzen™ 7 5800X", superscript: "3" },
                { icon: "bolt", label: "2 vCores @ ~4.7 GHz" },
                { icon: "memory", label: "12GB DDR5 RAM" },
                { icon: "storage", label: "60GB NVMe Storage", tooltip: "Free storage upgrades upto 400GB under fair usage policy." },
                { icon: "port", label: "5 Port Allocations" },
                { icon: "shield", label: "Enterprise Game DDoS Protection" },
            ],
            popular: false,
            checkoutId: 50,
        },
        {
            id: 9,
            name: "16GB RAM",
            tier: "nether",
            prices: { monthly: 6400, quarterly: 19200, yearly: 76800 },
            logoImage: "https://cdn.aurex.lk/minecraft-servers/plan_16gb.webp",
            specs: [
                { icon: "cpu", label: "AMD Ryzen™ 7 5800X", superscript: "3" },
                { icon: "bolt", label: "4 vCores @ ~4.7 GHz" },
                { icon: "memory", label: "16GB DDR5 RAM" },
                { icon: "storage", label: "60GB NVMe Storage", tooltip: "Free storage upgrades upto 400GB under fair usage policy." },
                { icon: "port", label: "5 Port Allocations" },
                { icon: "shield", label: "Enterprise Game DDoS Protection" },
            ],
            popular: false,
            checkoutId: 51,
        },
        {
            id: 10,
            name: "20GB RAM",
            tier: "diamond",
            prices: { monthly: 8000, quarterly: 24000, yearly: 96000 },
            logoImage: "https://cdn.aurex.lk/minecraft-servers/plan_20gb.webp",
            specs: [
                { icon: "cpu", label: "AMD Ryzen™ 7 5800X", superscript: "3" },
                { icon: "bolt", label: "4 vCores @ ~4.7 GHz" },
                { icon: "memory", label: "20GB DDR5 RAM" },
                { icon: "storage", label: "60GB NVMe Storage", tooltip: "Free storage upgrades upto 400GB under fair usage policy." },
                { icon: "port", label: "5 Port Allocations" },
                { icon: "shield", label: "Enterprise Game DDoS Protection" },
            ],
            popular: false,
            checkoutId: 52,
        },
        {
            id: 11,
            name: "24GB RAM",
            tier: "diamond",
            prices: { monthly: 9600, quarterly: 28800, yearly: 115200 },
            logoImage: "https://cdn.aurex.lk/minecraft-servers/plan_24gb.webp",
            specs: [
                { icon: "cpu", label: "AMD Ryzen™ 7 5800X", superscript: "3" },
                { icon: "bolt", label: "6 vCores @ ~4.7 GHz" },
                { icon: "memory", label: "24GB DDR5 RAM" },
                { icon: "storage", label: "60GB NVMe Storage", tooltip: "Free storage upgrades upto 400GB under fair usage policy." },
                { icon: "port", label: "5 Port Allocations" },
                { icon: "shield", label: "Enterprise Game DDoS Protection" },
            ],
            popular: false,
            checkoutId: 53,
        },
        {
            id: 12,
            name: "32GB RAM",
            tier: "diamond",
            prices: { monthly: 12800, quarterly: 38400, yearly: 153600 },
            logoImage: "https://cdn.aurex.lk/minecraft-servers/plan_32gb.webp",
            specs: [
                { icon: "cpu", label: "AMD Ryzen™ 7 5800X", superscript: "3" },
                { icon: "bolt", label: "8 vCores @ ~4.7 GHz" },
                { icon: "memory", label: "32GB DDR5 RAM" },
                { icon: "storage", label: "60GB NVMe Storage", tooltip: "Free storage upgrades upto 400GB under fair usage policy." },
                { icon: "port", label: "5 Port Allocations" },
                { icon: "shield", label: "Enterprise Game DDoS Protection" },
            ],
            popular: false,
            checkoutId: 54,
        },
        {
            id: 13,
            name: "64GB RAM",
            tier: "diamond",
            prices: { monthly: 25600, quarterly: 76800, yearly: 307200 },
            logoImage: "https://cdn.aurex.lk/minecraft-servers/plan_48gb.webp",
            specs: [
                { icon: "cpu", label: "AMD Ryzen™ 7 5800X", superscript: "3" },
                { icon: "bolt", label: "10 vCores @ ~4.7 GHz" },
                { icon: "memory", label: "64GB DDR5 RAM" },
                { icon: "storage", label: "60GB NVMe Storage", tooltip: "Free storage upgrades upto 400GB under fair usage policy." },
                { icon: "port", label: "5 Port Allocations" },
                { icon: "shield", label: "Enterprise Game DDoS Protection" },
            ],
            popular: false,
            outOfStock: true,
            checkoutId: 55,
        },
    ];

function SpecIcon({ type, label }: { type: string; label?: string }) {
    const cls = "w-3.5 h-3.5 shrink-0 text-muted-foreground/60";
    switch (type) {
        case "cpu":
            if (label?.includes("Ryzen")) return <BsAmd className={cls} />;
            if (label?.includes("Intel")) return <SiIntel className={cls} />;
            return <FiCpu className={cls} />;
        case "bolt":
            return <FiZap className={cls} />;
        case "memory":
            return <LuMemoryStick className={cls} />;
        case "storage":
            return <FiHardDrive className={cls} />;
        case "port":
            return <FiServer className={cls} />;
        case "shield":
            return <FiShield className={cls} />;
        default:
            return null;
    }
}


function PricingCard({
    name,
    tier,
    prices,
    logoImage,
    specs,
    popular,
    outOfStock,
    checkoutId,
    billingCycle,
    currency,
    rate,
    symbol,
}: {
    name: string;
    tier: Tier;
    prices: { monthly: number; quarterly: number; yearly: number };
    logoImage: string;
    specs: { icon: string; label: string; superscript?: string; tooltip?: string }[];
    popular: boolean;
    outOfStock?: boolean;
    checkoutId?: number;
    billingCycle: string;
    currency: Currency;
    rate: number;
    symbol: string;
}) {
    const [hovered, setHovered] = useState(false);
    const theme = tierConfig[tier];
    const rawPrice = prices[billingCycle as keyof typeof prices];
    const price = rawPrice * rate;

    const tierAccentHex: Record<Tier, string> = {
        grass: "#c084fc",
        nether: "#a855f7",
        diamond: "#7e22ce",
    };
    const accentHex = tierAccentHex[tier];

    return (
        <div
            className="relative h-full flex flex-col rounded-2xl overflow-visible"
            style={{
                transition: "box-shadow 0.3s ease",
                boxShadow: hovered ? theme.hoverShadow : "none",
                transformOrigin: "center center",
                willChange: "transform",
            }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <div
                className="absolute left-1/2 z-10 flex items-center justify-center"
                style={{
                    top: -36,
                    transform: "translateX(-50%)",
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    pointerEvents: "none",
                }}
            >
                <div
                    className="w-[68px] h-[68px] rounded-full flex items-center justify-center"
                >
                    <Image
                        src={logoImage}
                        alt={`${name} logo`}
                        width={96}
                        height={96}
                        className="w-24 h-auto rounded-xl object-contain"
                    />
                </div>
            </div>

            <div className={`relative h-full flex flex-col rounded-2xl overflow-hidden ${theme.cardGradient} border ${theme.border}`}>
                <div
                    className="absolute inset-0 opacity-[0.05] bg-cover bg-center pointer-events-none"
                    style={{ backgroundImage: "url('https://cdn.aurex.lk/games-background/minecraft-bg.webp')" }}
                />

                {outOfStock && (
                    <div className="absolute top-3 right-3 z-[2] px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-red-500/15 border border-red-500/40 text-red-400 backdrop-blur-sm">
                        Out of Stock
                    </div>
                )}

                <div className="relative z-[1] flex flex-col items-left pt-12 pb-3 px-4">
                    <div className="mt-2 flex flex-col items-left gap-0.5 mb-3">
                        <div className="flex items-left gap-1.5">
                            <span className="text-xs   font-medium px-4 py-0.5 rounded-br-lg rounded-tl-lg bg-primary">
                                {theme.label} Tier
                            </span>
                        </div>
                        <h3 className="text-2xl mt-2 font-bold text-foreground leading-tight">{name}</h3>
                    </div>

                    <div className="mb-3">
                        <span className="text-4xl font-extrabold" style={{ color: accentHex }}>{symbol}{formatPrice(price, currency)}</span>
                        <span className="text-muted-foreground text-sm">{billingPeriodLabel[billingCycle]}</span>
                    </div>
                    <div className="flex flex-col gap-2 w-full">
                        {specs.map((spec, i) => (
                            <div key={i} className="flex items-center gap-2">
                                <SpecIcon type={spec.icon} label={spec.label} />
                                <span className="text-xs text-muted-foreground flex items-center gap-1">
                                    {spec.label}
                                    {spec.superscript && (
                                        <sup className={`ml-0.5 text-[9px] ${theme.accent}`}>{spec.superscript}</sup>
                                    )}
                                    {spec.tooltip && (
                                        <span title={spec.tooltip} className="inline-flex items-center justify-center w-3 h-3 rounded-full border border-muted-foreground/60 text-[9px] cursor-help ml-1 font-bold">
                                            !
                                        </span>
                                    )}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="px-4 py-4 relative z-[1] mt-auto">
                    {outOfStock ? (
                        <button
                            type="button"
                            disabled
                            aria-disabled="true"
                            className="font-akira w-full rounded-xl py-2.5 text-sm font-semibold backdrop-blur-md bg-muted/40 text-muted-foreground border border-border/60 cursor-not-allowed"
                        >
                            OUT OF STOCK
                        </button>
                    ) : (
                        <a
                            href={
                                checkoutId !== undefined
                                    ? `https://dash.aurex.lk/market/8/checkout/${checkoutId}?duration=${billingCycle}&currency=${currency}&tab=server`
                                    : "https://dash.aurex.lk/market"
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-akira group relative w-full rounded-xl py-2.5 text-sm font-semibold transition-all duration-300 overflow-hidden backdrop-blur-md bg-primary/90 hover:bg-primary block text-center"
                        >
                            <span className="relative z-10">ORDER</span>
                        </a>
                    )}
                </div>
            </div>
        </div>
    );
}

function TierProgressBar({ activeIndex, onSelect }: { activeIndex: number; onSelect: (i: number) => void }) {
    const segOn: Record<Tier, string> = { grass: "bg-purple-400", nether: "bg-purple-500", diamond: "bg-purple-700" };
    const segOff: Record<Tier, string> = { grass: "bg-purple-400/20", nether: "bg-purple-500/20", diamond: "bg-purple-700/20" };

    return (
        <div className="mt-16 max-w-xs mx-auto">
            <div className="flex h-3">
                {packages.map((pkg, i) => (
                    <button
                        key={pkg.id}
                        aria-label={`Go to ${pkg.name}`}
                        onClick={() => onSelect(i)}
                        className={`flex-1 transition-all duration-300 ${i <= activeIndex ? segOn[pkg.tier] : segOff[pkg.tier]} ${i === 0 ? "rounded-l-full" : ""} ${i === packages.length - 1 ? "rounded-r-full" : ""}`}
                    />
                ))}
            </div>
        </div>
    );
}

function starFillPercent(slot: number, rating: number): number {
    if (slot <= Math.floor(rating)) return 100;
    if (slot > Math.ceil(rating)) return 0;
    return Math.round((rating % 1) * 100);
}

function TrustpilotWidget() {
    return (
        <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
                {Array.from({ length: TRUSTPILOT_MAX_STARS }, (_, i) => {
                    const fill = starFillPercent(i + 1, TRUSTPILOT_RATING);
                    return (
                        <div key={i} className="relative w-7 h-7 bg-gray-400/25 overflow-hidden">
                            <div
                                className="absolute inset-0 bg-[#00b67a] transition-all"
                                style={{ width: `${fill}%` }}
                            />
                            <span className="absolute inset-0 flex items-center justify-center text-white text-base leading-none select-none">
                                <SiTrustpilot title="Trustpilot Star" />
                            </span>
                        </div>
                    );
                })}
            </div>
            <div className="flex flex-col leading-tight">
                <span className="text-sm font-semibold text-foreground">
                    {TRUSTPILOT_RATING.toFixed(1)}{" "}
                    <span className="font-normal text-muted-foreground text-xs">/ {TRUSTPILOT_MAX_STARS}</span>
                </span>
                <span className="text-[11px] text-muted-foreground">
                    Based on{" "}
                    <span className="text-foreground font-medium">{TRUSTPILOT_REVIEWS.toLocaleString()}</span>{" "}
                    reviews
                </span>
            </div>
            <div className="hidden sm:flex items-center gap-1.5 pl-2 border-l border-border/50">
                <SiTrustpilot title="Trustpilot Logo" className="text-[#00b67a] w-6 h-6" />
                <span className="text-md">
                    <span className="text-foreground">Trust</span>
                    <span className="text-[#00b67a]">pilot</span>
                </span>
            </div>
        </div>
    );
}

export default function PricingSection() {
    const [billingCycle, setBillingCycle] = useState<"monthly" | "quarterly" | "yearly">("monthly");
    const [currency, setCurrency] = useState<Currency>("LKR");
    const { rate, symbol } = useCurrencyRate(currency);
    const [activeIndex, setActiveIndex] = useState(0);
    const scaleRafRef = useRef<number | null>(null);
    const [isFirst, setIsFirst] = useState(true);
    const [isLast, setIsLast] = useState(false);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(true);
    const [recommendedIndex, setRecommendedIndex] = useState(0);
    const [emblaRef, emblaApi] = useEmblaCarousel({
        loop: ENABLE_INFINITE_LOOP,
        align: "center",
        slidesToScroll: 1,
        containScroll: ENABLE_INFINITE_LOOP ? false : "keepSnaps",
    });
    const [emblaRef2, emblaApi2] = useEmblaCarousel({
        loop: false,
        align: "center",
        slidesToScroll: 1,
    });

    const scrollPrev = useCallback(() => { if (emblaApi) emblaApi.scrollPrev(); }, [emblaApi]);
    const scrollNext = useCallback(() => { if (emblaApi) emblaApi.scrollNext(); }, [emblaApi]);
    const scrollTo = useCallback((index: number) => { if (emblaApi) emblaApi.scrollTo(index); }, [emblaApi]);

    /**
     * Scale and fade partially-visible cards. Written straight to the DOM
     * rather than through React state: this fires on every scroll frame, and
     * re-rendering every card that often is what made dragging feel like it
     * was buffering. Rects are all read first, then styles all written, so the
     * loop never interleaves reads with writes and forces extra layouts.
     */
    const applyScales = useCallback(() => {
        if (!emblaApi) return;
        const nodes = emblaApi.slideNodes();
        const viewport = emblaApi.rootNode().getBoundingClientRect();
        const rects = nodes.map((node) => node.getBoundingClientRect());
        nodes.forEach((node, i) => {
            const card = node.firstElementChild as HTMLElement | null;
            if (!card) return;
            const r = rects[i];
            const fullyInside = r.left >= viewport.left - 2 && r.right <= viewport.right + 2;
            const visibleWidth = Math.max(0, Math.min(r.right, viewport.right) - Math.max(r.left, viewport.left));
            const scale = fullyInside ? 1 : Math.max(0.82, visibleWidth / r.width);
            card.style.transform = `scale(${scale})`;
            card.style.opacity = `${0.5 + scale * 0.5}`;
        });
    }, [emblaApi]);

    /** Coalesce bursts of scroll events into a single write per frame. */
    const scheduleScales = useCallback(() => {
        if (scaleRafRef.current !== null) return;
        scaleRafRef.current = requestAnimationFrame(() => {
            scaleRafRef.current = null;
            applyScales();
        });
    }, [applyScales]);

    const onSelect = useCallback(() => {
        if (!emblaApi) return;
        const index = emblaApi.selectedScrollSnap();
        setActiveIndex(index);
        if (!ENABLE_INFINITE_LOOP) {
            setIsFirst(index === 0);
            setIsLast(index === packages.length - 1);
            setCanScrollPrev(emblaApi.canScrollPrev());
            setCanScrollNext(emblaApi.canScrollNext());
        }
        scheduleScales();
    }, [emblaApi, scheduleScales]);

    useEffect(() => {
        if (!emblaApi) return;
        applyScales();
        emblaApi.on("select", onSelect);
        emblaApi.on("reInit", onSelect);
        emblaApi.on("scroll", scheduleScales);
        return () => {
            emblaApi.off("select", onSelect);
            emblaApi.off("reInit", onSelect);
            emblaApi.off("scroll", scheduleScales);
            if (scaleRafRef.current !== null) cancelAnimationFrame(scaleRafRef.current);
        };
    }, [emblaApi, onSelect, applyScales, scheduleScales]);

    /**
     * Pick the smallest orderable plan that covers the calculator's estimate.
     * Out-of-stock plans are skipped — recommending something nobody can buy
     * (the largest tier is frequently out of stock) is worse than recommending
     * the biggest plan that is actually available.
     */
    const handlePlanChange = useCallback((ram: number) => {
        const planRam = (p: typeof packages[number]) => {
            const match = p.name.match(/(\d+)\s*GB/i);
            return match ? parseInt(match[1]) : 0;
        };
        let targetIndex = packages.findIndex((p) => !p.outOfStock && planRam(p) >= ram);
        if (targetIndex === -1) {
            for (let i = packages.length - 1; i >= 0; i--) {
                if (!packages[i].outOfStock) { targetIndex = i; break; }
            }
        }
        if (targetIndex === -1) targetIndex = packages.length - 1;
        setRecommendedIndex(targetIndex);
        if (emblaApi2) emblaApi2.scrollTo(targetIndex);
    }, [emblaApi2]);

    const maskImage = ENABLE_INFINITE_LOOP
        ? "linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)"
        : isFirst && isLast
            ? "none"
            : isFirst
                ? "linear-gradient(to right, black 92%, transparent 100%)"
                : isLast
                    ? "linear-gradient(to right, transparent 0%, black 8%)"
                    : "linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)";

    return (
        <section className="relative overflow-hidden pb-16" id="pricing">
            <div className="relative z-10 mx-auto max-w-7xl pt-16 px-6">
                <div className="mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <TrustpilotWidget />
                    <div className="flex items-center gap-2 shrink-0">
                        <div className="backdrop-blur-xl border border-border/70 flex items-center gap-1 bg-white/5 rounded-full p-1">
                            {(["monthly", "quarterly", "yearly"] as const).map((cycle) => (
                                <button
                                    key={cycle}
                                    onClick={() => setBillingCycle(cycle)}
                                    className={`rounded-full px-3 py-2 text-sm font-medium capitalize transition-all ${billingCycle === cycle ? "bg-primary text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                                >
                                    {cycle}
                                </button>
                            ))}
                        </div>
                        <CurrencySelector value={currency} onChange={setCurrency} />
                    </div>
                </div>

                <div className="relative">
                    <button
                        onClick={scrollPrev}
                        onMouseDown={(e) => e.preventDefault()}
                        className="absolute left-0 sm:-left-4 top-1/2 -translate-y-1/2 z-20 flex h-10 w-10 items-center justify-center rounded-xl border-2 border-purple-400/70 backdrop-blur-xl bg-primary/60 transition-all"
                        style={{ visibility: canScrollPrev ? "visible" : "hidden" }}
                    >
                        <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M15 18l-6-6 6-6" />
                        </svg>
                    </button>
                    <button
                        onClick={scrollNext}
                        onMouseDown={(e) => e.preventDefault()}
                        className="absolute right-0 sm:-right-4 top-1/2 -translate-y-1/2 z-20 flex h-10 w-10 items-center justify-center rounded-xl border-2 border-purple-400/70 backdrop-blur-xl bg-primary/60 transition-all"
                        style={{ visibility: canScrollNext ? "visible" : "hidden" }}
                    >
                        <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M9 18l6-6-6-6" />
                        </svg>
                    </button>

                    <div
                        className="overflow-visible"
                        ref={emblaRef}
                        style={{
                            maskImage,
                            WebkitMaskImage: maskImage,
                            overflow: "hidden",
                        }}
                    >
                        <div className="flex items-center" style={{ paddingTop: "3rem", paddingBottom: "0.5rem" }}>
                            {packages.map((pkg) => (
                                <div
                                    key={pkg.id}
                                    className="flex-[0_0_100%] min-w-0 px-2 sm:flex-[0_0_50%] lg:flex-[0_0_25%]"
                                >
                                    <PricingCard
                                        {...pkg}
                                        billingCycle={billingCycle}
                                        currency={currency}
                                        rate={rate}
                                        symbol={symbol}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <TierProgressBar activeIndex={activeIndex} onSelect={scrollTo} />

                <div className="mt-20 border-t border-white/5 pt-16 flex flex-col md:flex-row items-center justify-between gap-12 w-full mx-auto">
                    <div className="flex-1 w-full flex flex-col justify-center">
                        <ResourceCalculator game="minecraft" onPlanChange={handlePlanChange} />
                    </div>
                    <div className="w-full sm:w-[320px] shrink-0 relative flex items-center justify-center">
                        <button
                            onClick={() => emblaApi2?.scrollPrev()}
                            className="absolute -left-5 z-10 w-10 h-10 flex items-center justify-center bg-[#0B0C10] border border-white/10 rounded-xl text-white hover:bg-white/5 hover:border-purple-500/50 transition-all shadow-[0_0_15px_rgba(0,0,0,0.5)]"
                        >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>
                        </button>

                        <div className="overflow-hidden w-full pt-12 pb-8 -mt-12 -mb-8" ref={emblaRef2} style={{ pointerEvents: 'none' }}>
                            <div className="flex items-stretch h-full">
                                {packages.map((pkg, i) => (
                                    <div key={pkg.id} className="flex-[0_0_100%] min-w-0 px-2" style={{ pointerEvents: 'auto' }}>
                                        {/* Fixed height so the badge never shifts card alignment. */}
                                        <div className="h-7 flex items-center justify-center">
                                            {i === recommendedIndex && (
                                                <span className="text-[10px] font-poppins uppercase tracking-wider px-2.5 py-1 rounded-full bg-purple-500/15 border border-purple-500/40 text-purple-300">
                                                    Recommended for you
                                                </span>
                                            )}
                                        </div>
                                        <PricingCard
                                            {...pkg}
                                            billingCycle={billingCycle}
                                            currency={currency}
                                            rate={rate}
                                            symbol={symbol}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>

                        <button
                            onClick={() => emblaApi2?.scrollNext()}
                            className="absolute -right-5 z-10 w-10 h-10 flex items-center justify-center bg-[#0B0C10] border border-white/10 rounded-xl text-white hover:bg-white/5 hover:border-purple-500/50 transition-all shadow-[0_0_15px_rgba(0,0,0,0.5)]"
                        >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
}